import React, { useState, useEffect } from "react";
import { Mic2, X, Save, Trash2, Music } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * Verified artists can submit their own plain-text + (optional) synced
 * .lrc lyrics for a track on their claimed artist page or their upload.
 */
const SubmitLyricsDialog = ({ open, onOpenChange, track, onSaved, existing }) => {
  const [plain, setPlain] = useState("");
  const [synced, setSynced] = useState("");
  const [busy, setBusy] = useState(false);
  const [showSynced, setShowSynced] = useState(false);

  useEffect(() => {
    if (open) {
      setPlain(existing?.plain || "");
      setSynced(existing?.synced || "");
      setShowSynced(!!existing?.synced);
    }
  }, [open, existing]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!plain.trim()) {
      toast.error("Lyrics text is required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/lyrics/custom", {
        track_id: track?.trackId ? String(track.trackId) : null,
        track_name: track?.trackName,
        artist_name: track?.artistName,
        artist_id: track?.artistId ? String(track.artistId) : null,
        album_name: track?.collectionName || null,
        plain: plain.trim(),
        synced: synced.trim() || null,
      });
      toast.success("Lyrics published");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save lyrics");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Remove your custom lyrics for this track?")) return;
    setBusy(true);
    try {
      await api.delete("/lyrics/custom", {
        params: {
          track_id: track?.trackId ? String(track.trackId) : undefined,
          track_name: track?.trackName,
          artist_id: track?.artistId ? String(track.artistId) : undefined,
        },
      });
      toast.success("Custom lyrics removed");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="submit-lyrics-dialog"
        className="bg-[#0a0a0a] border-white/10 text-white max-w-xl p-0 overflow-hidden max-h-[90vh] flex flex-col"
      >
        <DialogHeader className="px-5 py-4 border-b border-white/5 flex flex-row items-center justify-between shrink-0">
          <DialogTitle className="flex items-center gap-2 font-display text-lg min-w-0">
            <Mic2 size={18} className="text-[#E2FF31] shrink-0" />
            <span className="truncate">
              {existing ? "Edit lyrics" : "Submit lyrics"} — {track?.trackName}
            </span>
          </DialogTitle>
          <button
            type="button"
            data-testid="submit-lyrics-close"
            onClick={() => onOpenChange(false)}
            className="p-1.5 rounded-full hover:bg-white/10 transition shrink-0"
          >
            <X size={16} />
          </button>
        </DialogHeader>

        <form onSubmit={submit} className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/8">
            {track?.artworkUrl100 ? (
              <img src={track.artworkUrl100} alt="" className="w-10 h-10 rounded object-cover" />
            ) : (
              <div className="w-10 h-10 rounded bg-white/10 flex items-center justify-center">
                <Music size={16} className="text-white/60" />
              </div>
            )}
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate">{track?.trackName}</p>
              <p className="text-xs text-white/55 truncate">{track?.artistName}</p>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">
              Lyrics <span className="text-[#FF2E00]">*</span>
            </label>
            <textarea
              data-testid="submit-lyrics-plain"
              value={plain}
              onChange={(e) => setPlain(e.target.value)}
              rows={12}
              autoFocus
              maxLength={25000}
              placeholder={"[Verse 1]\nWrite your lyrics here…\n\n[Chorus]\n…"}
              className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-white/30 transition resize-y font-mono leading-relaxed"
            />
            <p className="text-[11px] text-white/40 text-right">{plain.length}/25000</p>
          </div>

          <button
            type="button"
            onClick={() => setShowSynced((s) => !s)}
            data-testid="submit-lyrics-toggle-synced"
            className="text-xs text-[#E2FF31] hover:underline"
          >
            {showSynced ? "Hide" : "Add"} time-synced LRC (optional)
          </button>

          {showSynced && (
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">
                Synced lyrics (LRC format)
              </label>
              <textarea
                data-testid="submit-lyrics-synced"
                value={synced}
                onChange={(e) => setSynced(e.target.value)}
                rows={6}
                placeholder={"[00:12.45]First line of the song\n[00:16.10]Next line"}
                className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-white/30 transition resize-y font-mono"
              />
              <p className="text-[11px] text-white/40">
                Each line: <code className="text-white/60">[mm:ss.xx]Lyric text</code>
              </p>
            </div>
          )}

          <div className="text-xs text-white/55 px-3 py-2 rounded-lg bg-[#E2FF31]/5 border border-[#E2FF31]/15">
            Only submit lyrics you wrote or have the right to publish. By submitting, you confirm
            you&apos;re the verified artist for this track.
          </div>
        </form>

        <div className="px-5 py-3 border-t border-white/5 flex items-center justify-between gap-2 shrink-0">
          {existing ? (
            <button
              type="button"
              data-testid="submit-lyrics-delete"
              onClick={handleDelete}
              disabled={busy}
              className="text-xs text-white/50 hover:text-[#FF2E00] flex items-center gap-1.5 disabled:opacity-50"
            >
              <Trash2 size={13} /> Remove
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="text-sm text-white/60 hover:text-white px-4 py-2 rounded-full hover:bg-white/5 transition"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              data-testid="submit-lyrics-save"
              disabled={busy || !plain.trim()}
              className="bg-[#FF2E00] hover:bg-[#E62900] disabled:opacity-50 text-white font-semibold rounded-full px-5 py-2 text-sm transition flex items-center gap-2"
            >
              <Save size={14} /> {busy ? "Saving…" : "Publish lyrics"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SubmitLyricsDialog;
